import * as React from "react";

import { cn } from "@/lib/utils/cn";
import { GROUP_SEPARATOR, groupOnInput } from "@/lib/utils/money";

/**
 * An amount box: digits grouped by three as you type, a `$` suffix, no spinners.
 *
 * A `type="number"` input can't display a grouped value (the browser rejects it),
 * which is also why the native spinner arrows are gone. The value stays a display
 * string — convert with `amountToApi` before sending it anywhere.
 *
 * Counterpart of the Django app's `MoneyInput` widget + `money-input.js`.
 */
export const MoneyInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<"input">, "type" | "onChange" | "value"> & {
    value: string;
    onValueChange: (value: string) => void;
    /** Set when the label is a sibling rather than a wrapper, so the unit is announced. */
    unitId?: string;
  }
>(({ className, value, onValueChange, unitId, onKeyDown, ...props }, ref) => {
  const reformat = (
    el: HTMLInputElement,
    text: string,
    caret: number,
  ) => {
    const next = groupOnInput(text, caret);
    // Write the DOM first, then the caret, then state — with the same string.
    // React skips a redundant DOM write when node.value already matches, so the
    // caret survives the re-render, and a no-op change (no re-render at all) is
    // still reflected because we wrote the element directly.
    el.value = next.value;
    try {
      el.setSelectionRange(next.caret, next.caret);
    } catch {
      // Some browsers refuse on a hidden input; the value is what matters.
    }
    onValueChange(next.value);
  };

  return (
    <span className="relative block">
      <input
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        aria-describedby={unitId}
        onChange={(e) => {
          const el = e.currentTarget;
          reformat(el, el.value, el.selectionStart ?? el.value.length);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          // Backspace right after a group separator would read as a no-op: the
          // digit goes, the reflow puts the separator back. Eat both at once.
          if (e.key !== "Backspace" || e.defaultPrevented) return;
          const el = e.currentTarget;
          const caret = el.selectionStart;
          if (caret === null || caret !== el.selectionEnd) return;
          if (caret < 2 || el.value[caret - 1] !== GROUP_SEPARATOR) return;
          e.preventDefault();
          reformat(el, el.value.slice(0, caret - 2) + el.value.slice(caret), caret - 2);
        }}
        className={cn(
          "w-full rounded-md border border-border px-3 py-2 pr-8",
          className,
        )}
      />
      <span
        id={unitId}
        aria-hidden={unitId ? undefined : true}
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground"
      >
        $
      </span>
    </span>
  );
});
MoneyInput.displayName = "MoneyInput";
